import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Table, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { getAvailableAdvance, createAdvance, markAdvancePaid, toggleAdvancePause, deleteAdvance, getAdvances } from '../api';
import SearchableSelect from './SearchableSelect';
import { toast } from 'react-toastify';
import { DollarSign, CreditCard, RefreshCw, PauseCircle, PlayCircle, Trash2 } from 'lucide-react';

const AdvancesView = ({ vehicles }) => {
    const [driverId, setDriverId] = useState('');
    const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
    const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
    const [availableAmount, setAvailableAmount] = useState(null);
    const [amountToAdvance, setAmountToAdvance] = useState('');
    const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    
    const [advances, setAdvances] = useState([]);
    const [isLoadingAdvances, setIsLoadingAdvances] = useState(false);

    useEffect(() => {
        loadAdvances();
    }, []);

    const loadAdvances = async () => {
        setIsLoadingAdvances(true);
        try {
            const data = await getAdvances();
            setAdvances(data);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoadingAdvances(false);
        }
    };

    const handleCalculateAvailable = async () => {
        if (!driverId) {
            toast.warning("Selecciona un conductor");
            return;
        }
        setIsLoadingAvailable(true);
        try {
            const data = await getAvailableAdvance(driverId, startDate, endDate);
            setAvailableAmount(data.available_amount);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoadingAvailable(false);
        }
    };

    const handleCreateAdvance = async () => {
        if (!driverId || !amountToAdvance || amountToAdvance <= 0) {
            toast.warning("Completa todos los campos");
            return;
        }
        if (availableAmount !== null && amountToAdvance > availableAmount) {
            toast.warning("El monto supera lo disponible");
            return;
        }
        
        setIsCreating(true);
        try {
            await createAdvance({
                driver_id: parseInt(driverId),
                start_date: startDate,
                end_date: endDate,
                amount: parseFloat(amountToAdvance)
            });
            toast.success("Anticipo generado exitosamente");
            setAmountToAdvance('');
            setAvailableAmount(null);
            loadAdvances();
        } catch (e) {
            console.error(e);
        } finally {
            setIsCreating(false);
        }
    };

    const handleMarkAsPaid = async (advanceId) => {
        try {
            await markAdvancePaid(advanceId);
            toast.success("Anticipo marcado como pagado");
            loadAdvances();
        } catch (e) {
            console.error(e);
        }
    };

    const handleTogglePause = async (adv) => {
        try {
            const res = await toggleAdvancePause(adv.id);
            toast.info(res.is_paused ? "⏸️ Descuento en pausa: no entrará en preliquidación" : "▶️ Descuento reactivado: se descontará en la próxima liquidación");
            loadAdvances();
        } catch (e) {
            console.error(e);
        }
    };

    const handleDeleteAdvance = async (adv) => {
        const msg = `¿Eliminar definitivamente el registro A-${String(adv.id).padStart(3, '0')} (${formatCurrency(adv.amount)}) de ${adv.driver?.full_name || 'este conductor'}?`;
        if (!window.confirm(msg)) return;
        try {
            await deleteAdvance(adv.id);
            toast.success("Registro eliminado correctamente");
            loadAdvances();
        } catch (e) {
            console.error(e);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(val);
    };

    const driverOptions = [];
    const seenDrivers = new Set();
    vehicles.forEach(v => {
        if (v.current_driver_id && !seenDrivers.has(v.current_driver_id)) {
            seenDrivers.add(v.current_driver_id);
            driverOptions.push({
                id: v.current_driver_id,
                name: `${v.internal_code} - ${v.current_driver?.full_name || 'Sin Conductor'}`
            });
        }
    });

    const getVehicleCode = (driverId) => {
        const v = vehicles.find(vec => vec.current_driver_id === driverId);
        return v ? v.internal_code : 'S/M';
    };

    const isCreditReimbursement = (adv) => {
        return (parseFloat(adv?.amount) || 0) < 0;
    };

    const isPenaltyDeduction = (adv) => {
        if (isCreditReimbursement(adv)) return false;
        const n = (adv.notes || '').toLowerCase();
        return n.includes('deducción') || n.includes('disputa') || n.includes('multa') || n.includes('penal');
    };

    // Lógica de estado del anticipo / ajuste:
    // - ABONADO / ABONO PENDIENTE: saldo a favor del conductor (amount < 0, ej. reversa Booking)
    // - DESCONTADO (Liq. #X): ya fue incluido en una liquidación (settlement_id != null)
    // - EN PAUSA (RETENIDO): retenido temporalmente (ej. disputa escalada), no entra a preliquidación
    // - PAGADO: fue pagado al conductor directamente (is_paid = true)
    // - PENDIENTE: ni pagado ni descontado aún
    const getAdvanceStatus = (adv) => {
        const isCredit = isCreditReimbursement(adv);
        if (adv.settlement_id) {
            return isCredit
                ? { label: `ABONADO (Liq. #${adv.settlement_id})`, bg: 'success', textColor: 'success' }
                : { label: `DESCONTADO (Liq. #${adv.settlement_id})`, bg: 'info', textColor: 'info' };
        }
        if (adv.is_paused) {
            return { label: '⏸️ EN PAUSA (RETENIDO)', bg: 'secondary', textColor: 'secondary' };
        }
        if (isCredit) {
            return { label: '🏆 ABONO PENDIENTE', bg: 'success', textColor: 'success' };
        }
        if (adv.is_paid) {
            return { label: 'PAGADO', bg: 'success', textColor: 'success' };
        }
        return { label: 'PENDIENTE', bg: 'warning', textColor: 'warning' };
    };

    return (
        <div className="p-3">
            <Row className="mb-4">
                <Col md={12}>
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body>
                            <h5 className="fw-bold text-slate-700 mb-3">Generar Nuevo Anticipo</h5>
                            <Row className="align-items-end g-3">
                                <Col md={4}>
                                    <Form.Label className="small fw-bold text-slate-600">Conductor / Móvil</Form.Label>
                                    <SearchableSelect 
                                        options={driverOptions} 
                                        value={driverId} 
                                        onChange={val => setDriverId(val)} 
                                        placeholder="Selecciona Conductor..."
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="small fw-bold text-slate-600">Desde</Form.Label>
                                    <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="small fw-bold text-slate-600">Hasta</Form.Label>
                                    <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                                </Col>
                                <Col md={2}>
                                    <Button variant="outline-primary" className="w-100 fw-bold" onClick={handleCalculateAvailable} disabled={isLoadingAvailable}>
                                        {isLoadingAvailable ? <Spinner size="sm" /> : <RefreshCw size={16} className="me-1" />} Calcular
                                    </Button>
                                </Col>
                            </Row>

                            {availableAmount !== null && (
                                <div className="mt-4 p-3 bg-slate-50 rounded-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <span className="text-muted small">Monto Disponible:</span>
                                            <h3 className="text-primary fw-bold mb-0">{formatCurrency(availableAmount)}</h3>
                                        </div>
                                        <div className="text-end">
                                            <span className="text-muted small">Costo Financiero (2%):</span>
                                            <h5 className="text-danger mb-0">-{formatCurrency(amountToAdvance * 0.02)}</h5>
                                        </div>
                                    </div>
                                    
                                    <Row className="align-items-end g-2">
                                        <Col md={8}>
                                            <Form.Label className="small fw-bold text-slate-600">Monto a Anticipar</Form.Label>
                                            <div className="position-relative">
                                                <DollarSign size={18} className="position-absolute top-50 translate-middle-y text-slate-400" style={{ left: '12px' }} />
                                                <Form.Control 
                                                    type="number" 
                                                    value={amountToAdvance} 
                                                    onChange={e => setAmountToAdvance(e.target.value)} 
                                                    placeholder="0"
                                                    className="ps-4"
                                                    max={availableAmount}
                                                    isInvalid={availableAmount !== null && parseFloat(amountToAdvance) > availableAmount}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    El monto no puede superar lo disponible ({formatCurrency(availableAmount)}).
                                                </Form.Control.Feedback>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <Button variant="success" className="w-100 fw-bold" onClick={handleCreateAdvance} disabled={isCreating || !amountToAdvance || availableAmount === null || parseFloat(amountToAdvance) > availableAmount}>
                                                {isCreating ? <Spinner size="sm" /> : <CreditCard size={16} className="me-1" />} Solicitar Anticipo
                                            </Button>
                                        </Col>
                                    </Row>
                                    <div className="mt-2 small text-muted">
                                        * Se generará un egreso en el flujo por el 98% del monto solicitado. El 2% se registrará como comisión.
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <Card.Header className="bg-white p-3 border-0">
                    <h6 className="fw-bold text-slate-700 mb-0">Historial de Anticipos, Descuentos y Abonos</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table hover responsive className="mb-0 align-middle">
                        <thead className="bg-slate-50 border-bottom">
                            <tr>
                                <th className="py-3 px-4 text-slate-500 small text-uppercase fw-bold">ID</th>
                                <th className="py-3 text-slate-500 small text-uppercase fw-bold">Conductor / Móvil</th>
                                <th className="py-3 text-slate-500 small text-uppercase fw-bold">Periodo / Concepto</th>
                                <th className="py-3 text-end text-slate-500 small text-uppercase fw-bold">Monto</th>
                                <th className="py-3 text-end text-slate-500 small text-uppercase fw-bold">Costo (2%)</th>
                                <th className="py-3 text-end text-slate-500 small text-uppercase fw-bold">Monto Neto</th>
                                <th className="py-3 text-center text-slate-500 small text-uppercase fw-bold">Estado</th>
                                <th className="py-3 text-center text-slate-500 small text-uppercase fw-bold pe-4">Acción</th>
                            </tr>
                        </thead>
                        <tbody className="border-0">
                            {isLoadingAdvances ? (
                                <tr><td colSpan="8" className="text-center py-4"><Spinner /></td></tr>
                            ) : advances.length === 0 ? (
                                <tr><td colSpan="8" className="text-center py-4 text-muted">No hay anticipos registrados.</td></tr>
                            ) : (
                                advances.map((adv) => {
                                    const isCredit = isCreditReimbursement(adv);
                                    const isPenalty = isPenaltyDeduction(adv);
                                    const absAmt = Math.abs(parseFloat(adv.amount) || 0);
                                    const comision = (isPenalty || isCredit) ? 0 : absAmt * 0.02;
                                    const neto = absAmt - comision;
                                    const status = getAdvanceStatus(adv);
                                    const alreadySettled = !!adv.settlement_id;
                                    return (
                                        <tr key={adv.id} className={alreadySettled ? 'table-light' : adv.is_paused ? 'opacity-75' : ''}>
                                            <td className="px-4 fw-bold text-primary">A-{String(adv.id).padStart(3, '0')}</td>
                                            <td>
                                                <div className="fw-bold text-slate-700">{adv.driver?.full_name || 'N/A'}</div>
                                                <small className="text-muted">Móvil: {getVehicleCode(adv.driver_id)}</small>
                                            </td>
                                            <td className="small" style={{ maxWidth: '300px' }}>
                                                <div>{adv.start_date} al {adv.end_date}</div>
                                                {adv.notes && (
                                                    <div className={`mt-1 ${isCredit ? 'text-success fw-medium' : 'text-muted'}`} style={{ fontSize: '0.78rem', lineHeight: '1.25' }}>
                                                        {adv.notes}
                                                    </div>
                                                )}
                                            </td>
                                            <td className={`text-end fw-bold ${isCredit ? 'text-success' : ''}`}>
                                                {isCredit ? `+${formatCurrency(absAmt)}` : formatCurrency(adv.amount)}
                                            </td>
                                            <td className="text-end text-danger">{(isPenalty || isCredit) ? '—' : `-${formatCurrency(comision)}`}</td>
                                            <td className="text-end fw-bold text-success">
                                                {isCredit ? `+${formatCurrency(neto)}` : formatCurrency(neto)}
                                            </td>
                                            <td className="text-center">
                                                <Badge
                                                    bg={status.bg}
                                                    className={`px-2 py-1 bg-opacity-10 text-${status.textColor} border border-${status.textColor} border-opacity-25`}
                                                >
                                                    {status.label}
                                                </Badge>
                                            </td>
                                            <td className="text-center pe-4">
                                                {alreadySettled ? (
                                                    <span className="small text-muted fst-italic">{isCredit ? 'Abonado' : 'Descontado'}</span>
                                                ) : (
                                                    <div className="d-flex justify-content-center align-items-center gap-1 flex-wrap">
                                                        {!adv.is_paid && !adv.is_paused && !isPenalty && !isCredit && (
                                                            <Button variant="outline-success" size="sm" className="fw-bold" onClick={() => handleMarkAsPaid(adv.id)}>
                                                                Marcar Pagado
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant={adv.is_paused ? "outline-primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="fw-bold d-inline-flex align-items-center gap-1"
                                                            onClick={() => handleTogglePause(adv)}
                                                            title={adv.is_paused ? "Reactivar para aplicar en liquidación" : "Pausar/retener para NO incluir en preliquidación"}
                                                        >
                                                            {adv.is_paused ? <><PlayCircle size={14} /> Reactivar</> : <><PauseCircle size={14} /> Pausar</>}
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteAdvance(adv)}
                                                            title="Eliminar registro"
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdvancesView;
